import type React from 'react';
import { useState } from 'react';
import { Button, FormField, FormHelp } from '@zillow/constellation-10';
import { IconDownloadOutline } from '@zillow/constellation-icons';
import { pdf } from '@react-pdf/renderer';
import { logError } from '@zg-rentals/log-error';
import { useAppSelector } from '../flux/configureStore';
import PDFReport from '../PDF';
import api from '../utils/api';
import { saveFile } from '../utils/fileUtils';
import { getFilterLabels } from '../utils/filterUtils';
import { getStaticMapUrl } from '../utils/mapUtils';

import type Address from '../models/Address';
import type { State as ComparablesState } from '../flux/reducers/comparables';
import type { RootState } from '../flux/reducers';
import { emitDownloadPDFEvent } from '../utils/analytics/ClickstreamEvents';

interface DownloadPDFButtonProps {
  width?: number;
}

function getFileName(listing: Address): string {
  const { street } = listing;
  if (street) {
    return `HomeReport-${street.split(' ').join('-')}.pdf`;
  }
  return 'HomeReport.pdf';
}

const DownloadPDFButtonComponent: React.FunctionComponent<
  React.PropsWithChildren<React.PropsWithChildren<DownloadPDFButtonProps>>
> = (props) => {
  const { width } = props;

  const user = useAppSelector((state: RootState) => state.user);
  const comparablesState: ComparablesState = useAppSelector((state: RootState) => state.comparables);
  const listing: Address = useAppSelector((state: RootState) => state.address);
  const filter = useAppSelector((state: RootState) => state.filter);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const onClick = async () => {
    try {
      setLoading(true);
      const unsignedUrl = getStaticMapUrl(listing, comparablesState.items);
      const { signedUrl } = await api.signUrl(unsignedUrl);
      const blob = await pdf(
        <PDFReport
          listing={listing}
          comparablesState={comparablesState}
          filterLabels={getFilterLabels(filter)}
          customPrice={filter.price}
          signedMapUrl={signedUrl}
        />,
      ).toBlob();
      setLoading(false);
      saveFile(blob, getFileName(listing));
      emitDownloadPDFEvent({ user });
    } catch (e) {
      logError({
        error: e,
        errorType: 'PmrDownloadPdfError',
        context: {
          listing,
          filter,
        },
      });
      setLoading(false);
      setError(true);
    }
  };

  return (
    comparablesState.items.length > 1 && (
      <FormField>
        <Button
          loading={loading}
          size="sm"
          tone="brand"
          emphasis="outlined"
          icon={<IconDownloadOutline />}
          style={{ ...(width && { width: width }) }}
          css={{ marginLeft: '200', height: '1100' }}
          onClick={onClick}
        >
          PDF
        </Button>
        {error && <FormHelp error>An unexpected error occurred</FormHelp>}
      </FormField>
    )
  );
};

const DownloadPDFButton: React.FunctionComponent<
  React.PropsWithChildren<React.PropsWithChildren<DownloadPDFButtonProps>>
> = (props) => {
  if (typeof window === 'undefined') {
    return null;
  }
  return <DownloadPDFButtonComponent {...props} />;
};

export default DownloadPDFButton;
